






















import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import BarChart from '@/components/charts/BarChart.vue';
import { getParcelCycle, getParcelStatus, parcelMatchFilters } from '@/services/analyticData';
import { DashboardAnalyticData } from '@/interfaces/dashboardAnalyticData';
import { CHART_TYPE } from '@/services/charts';
import { Parcel } from '@/interfaces/parcel';
import { stringToMomentDate } from '@/services/date';
import { ProductType } from '@/enums/productType';
import { ProductGroupName } from '@/enums/productGroupName';

interface HarvestedInfo {
  harvested: {
    [key: string]: Parcel;
  };
  partialHarvested: {
    [key: string]: Parcel;
  };
}

@Component({
  components: {
    BarChart
  }
})
export default class AnalyticsBarChart extends Vue {
  @Prop() analytics: DashboardAnalyticData[];
  @Prop() label: string;
  @Prop() yAxesMax: number;
  @Prop() hideNumberOfCycles: boolean;
  @Prop() showTotal: boolean;

  selectedType: string = CHART_TYPE.VARIETY;
  CHART_TYPE = CHART_TYPE;
  productGroupName = ProductGroupName;

  chartData = null;
  chartOptions = null;

  mounted(): void {
    this.setupChartData();
  }

  @Watch('analytics')
  onAnalyticDataChanged(): void {
    this.setupChartData();
  }

  setupChartData(): void {
    const isVegetativeIndex = this.$store.state.analytic.selectedProductType === ProductType.VEGETATIVE_INDEX;

    if (isVegetativeIndex) {
      this.setupStackedChartData();
    } else {
      this.setupRegularChartData();
    }
  }

  private setupStackedChartData(): void {
    const data = this.getStackedChartData();
    const options = {
      maintainAspectRatio: false,
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          fontColor: '#4E596D',
          padding: 15,
          usePointStyle: true
        }
      },
      tooltips: {
        enabled: true,
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function (tooltipItem, data) {
            const datasetLabel = data.datasets[tooltipItem.datasetIndex].label || '';
            const value = tooltipItem.yLabel;
            return datasetLabel + ': ' + Math.round(value) + ' parcels';
          }
        }
      },
      plugins: {
        datalabels: {
          display: false
        }
      },
      scales: {
        yAxes: [
          {
            stacked: true,
            ticks: {
              fontColor: '#4E596D',
              min: 0,
              callback: function (value) {
                if (Number.isInteger(value)) {
                  return value;
                }
              }
            },
            scaleLabel: {
              display: true,
              labelString: 'Number of Parcels',
              fontColor: '#4E596D'
            }
          }
        ],
        xAxes: [
          {
            stacked: true,
            ticks: {
              fontColor: '#4E596D'
            }
          }
        ]
      }
    };

    this.chartOptions = options;
    this.chartData = data;
  }

  private setupRegularChartData(): void {
    const data = this.getChartData();
    const options = {
      maintainAspectRatio: false,
      legend: {
        display: false
      },
      tooltips: {
        enabled: true
      },
      scales: {
        yAxes: [
          {
            ticks: {
              fontColor: '#4E596D',
              min: 0,
              max: this.yAxesMax || Math.ceil(Math.max(...data.datasets[0].data) / 10) * 10 + 10
            }
          }
        ],
        xAxes: [
          {
            ticks: {
              fontColor: '#4E596D'
            }
          }
        ]
      }
    };

    this.chartOptions = options;
    this.chartData = data;
  }

  private getStackedChartData(): any {
    const data = {};

    this.analytics.forEach((analyticData: DashboardAnalyticData) => {
      let parcel;
      if (this.$store.state.analytic.selectedProductType === ProductType.VEGETATIVE_INDEX) {
        parcel = (Object.values(this.$store.state.parcels) as Parcel[]).find((p) => p.Name === analyticData.ParcelID);
      } else {
        parcel = this.$store.state.parcels[analyticData.ParcelID];
      }

      const momentTo = stringToMomentDate(this.$store.state.analytic.fromTo.to);

      if (parcel) {
        if (
          parcel &&
          parcel.HarvestDates &&
          parcel.HarvestDates.length &&
          parcelMatchFilters(parcel, this.$store.state.mapParcelFilters, this.$store.state.analytic.fromTo) &&
          momentTo.isSameOrAfter(stringToMomentDate(parcel.HarvestDates[0]))
        ) {
          return;
        }

        const groupKey = this.getGroupKey(parcel);
        if (groupKey) {
          if (!data[groupKey]) {
            data[groupKey] = {
              range_0_25: 0,
              range_25_50: 0,
              range_50_75: 0,
              range_75_100: 0,
              totalCount: 0
            };
          }

          if (analyticData.categoryCount) {
            data[groupKey].range_0_25 += analyticData.categoryCount.range_0_25 || 0;
            data[groupKey].range_25_50 += analyticData.categoryCount.range_25_50 || 0;
            data[groupKey].range_50_75 += analyticData.categoryCount.range_50_75 || 0;
            data[groupKey].range_75_100 += analyticData.categoryCount.range_75_100 || 0;
            data[groupKey].totalCount += 1;
          }
        }
      }
    });

    const labels = Object.keys(data)
      .sort((a: string, b: string) => (data[a].totalCount > data[b].totalCount ? -1 : 1))
      .slice(0, 20)
      .sort((a: string, b: string) => {
        if (this.selectedType === CHART_TYPE.NUMBER_OF_CYCLES) {
          return parseInt(a) > parseInt(b) ? 1 : -1;
        }
        return a > b ? 1 : -1;
      });

    const datasets = [
      {
        label: this.$t('Excellent') || 'Excellent',
        backgroundColor: '#097969',
        barThickness: 15,
        data: labels.map((name) => data[name].range_75_100)
      },
      {
        label: this.$t('Good') || 'Good',
        backgroundColor: '#50c878',
        barThickness: 15,
        data: labels.map((name) => data[name].range_50_75)
      },
      {
        label: this.$t('Average') || 'Average',
        backgroundColor: '#ffff00',
        barThickness: 15,
        data: labels.map((name) => data[name].range_25_50)
      },
      {
        label: this.$t('Poor') || 'Poor',
        backgroundColor: '#ff0000',
        barThickness: 15,
        data: labels.map((name) => data[name].range_0_25)
      }
    ];

    return {
      datasets,
      labels: labels.map((label) => (label === 'undefined' ? this.$t('undefined') : label))
    };
  }

  private getGroupKey(parcel: Parcel): string | null {
    if (this.selectedType === CHART_TYPE.VARIETY) {
      return parcel.Variety;
    }
    if (this.selectedType === CHART_TYPE.CROP_SEASON && parcel.CropSeason !== undefined) {
      return parcel.CropSeason;
    }
    if (this.selectedType === CHART_TYPE.STATUS) {
      return getParcelStatus(
        parcel,
        this.$store.state.analytic.fromTo.to,
        this.$root.$i18n,
        this.$store.state.selectedCountry
      );
    }
    if (this.selectedType === CHART_TYPE.FARM_NAME && this.$store.state.selectedUnitHierarchy) {
      const farm = this.$store.state.selectedUnitHierarchy.Farms.find((farm) => farm.id === parcel.FarmID);
      return farm ? farm.Name : null;
    }
    if (this.selectedType === CHART_TYPE.NUMBER_OF_CYCLES) {
      return null;
    }
    return null;
  }

  private getChartData(): any {
    const data = {};
    let numberOfParcels = [];
    let listOfParcels = [];
    let countParcels;
    this.analytics.forEach((analyticData: DashboardAnalyticData) => {
      listOfParcels.push(analyticData.ParcelID);
      let parcel;
      if (this.$store.state.analytic.selectedProductType === ProductType.VEGETATIVE_INDEX) {
        parcel = (Object.values(this.$store.state.parcels) as Parcel[]).find((p) => p.Name === analyticData.ParcelID);
      } else {
        parcel = this.$store.state.parcels[analyticData.ParcelID];
      }
      numberOfParcels.push(parcel);
      const momentTo = stringToMomentDate(this.$store.state.analytic.fromTo.to);
      if (parcel) {
        if (
          parcel &&
          parcel.HarvestDates &&
          parcel.HarvestDates.length &&
          parcelMatchFilters(parcel, this.$store.state.mapParcelFilters, this.$store.state.analytic.fromTo) &&
          momentTo.isSameOrAfter(stringToMomentDate(parcel.HarvestDates[0]))
        ) {
          countParcels = null;
        } else {
          if (this.selectedType === CHART_TYPE.VARIETY) {
            data[parcel.Variety] = data[parcel.Variety] || { totalValue1: 0, totalValue2: 0, totalArea: 0 };
            data[parcel.Variety].totalValue1 += analyticData.value1;
            data[parcel.Variety].totalValue2 += analyticData.value2;
            data[parcel.Variety].totalArea += parcel.Area;
          }
          if (this.selectedType === CHART_TYPE.CROP_SEASON && parcel.CropSeason !== undefined) {
            data[parcel.CropSeason] = data[parcel.CropSeason] || { totalValue1: 0, totalValue2: 0, totalArea: 0 };
            data[parcel.CropSeason].totalValue1 += analyticData.value1;
            data[parcel.CropSeason].totalValue2 += analyticData.value2;
            data[parcel.CropSeason].totalArea += parcel.Area;
          }

          if (this.selectedType === CHART_TYPE.STATUS) {
            const status = getParcelStatus(
              parcel,
              this.$store.state.analytic.fromTo.to,
              this.$root.$i18n,
              this.$store.state.selectedCountry
            );
            data[status] = data[status] || { totalValue1: 0, totalValue2: 0, totalArea: 0 };
            data[status].totalValue1 += analyticData.value1;
            data[status].totalValue2 += analyticData.value2;
            data[status].totalArea += parcel.Area;
          }
          if (this.selectedType === CHART_TYPE.FARM_NAME && this.$store.state.selectedUnitHierarchy) {
            const farm = this.$store.state.selectedUnitHierarchy.Farms.find((farm) => farm.id === parcel.FarmID);
            if (farm) {
              data[farm.Name] = data[farm.Name] || { totalValue1: 0, totalValue2: 0, totalArea: 0 };
              data[farm.Name].totalValue1 += analyticData.value1;
              data[farm.Name].totalValue2 += analyticData.value2;
              data[farm.Name].totalArea += parcel.Area;
            }
          }
          if (this.selectedType === CHART_TYPE.NUMBER_OF_CYCLES) {
            const cycle = getParcelCycle(parcel, analyticData.SurveyDate);
            data[cycle] = data[cycle] || { totalValue1: 0, totalValue2: 0, totalArea: 0 };
            data[cycle].totalValue1 += analyticData.value1;
            data[cycle].totalValue2 += analyticData.value2;
            data[cycle].totalArea += parcel.Area;
          }
        }
      }
    });

    const labels = Object.keys(data)
      .sort((a: string, b: string) => (data[a].totalArea > data[b].totalArea ? -1 : 1))
      .slice(0, 20)
      .sort((a: string, b: string) => {
        if (this.selectedType === CHART_TYPE.NUMBER_OF_CYCLES) {
          return parseInt(a) > parseInt(b) ? 1 : -1;
        }
        return a > b ? 1 : -1;
      });

    const datasets = [
      {
        datalabels: {
          color: '#161c26',
          align: 'top',
          anchor: 'end'
        },
        backgroundColor: '#029973',
        barThickness: 20,
        data: labels.map((name) => {
          if (this.showTotal) {
            return Math.round(data[name].totalValue1 * 100) / 100;
          }
          return data[name].totalValue2
            ? Math.round((data[name].totalValue1 / data[name].totalValue2) * 100 * 100) / 100
            : 0;
        })
      }
    ];

    return { datasets, labels: labels.map((label) => (label === 'undefined' ? this.$t('undefined') : label)) };
  }

  private getHarvestedInfo(): HarvestedInfo {
    if (
      this.$store.state.analytic.selectedProductType === ProductType.SUGAR_CONTENT_PREDICTION ||
      this.$store.state.analytic.selectedProductType === ProductType.SUGAR_CONTENT_YIELD
    ) {
      return this.getSugarContentHarvestedInfo();
    }
    return null;
  }

  private getSugarContentHarvestedInfo(): HarvestedInfo {
    const harvested = {};
    const momentTo = stringToMomentDate(this.$store.state.analytic.fromTo.to);
    this.$store.getters.filteredParcelIds.forEach((parcelId: string) => {
      const parcel = this.$store.state.parcels[parcelId] as Parcel;
      if (
        parcel &&
        parcel.HarvestDates &&
        parcel.HarvestDates.length &&
        parcelMatchFilters(parcel, this.$store.state.mapParcelFilters, this.$store.state.analytic.fromTo) &&
        momentTo.isSameOrAfter(stringToMomentDate(parcel.HarvestDates[0]))
      ) {
        harvested[parcel.id] = parcel;
      }
    });

    return {
      harvested,
      partialHarvested: null
    };
  }
}
